"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { BlockBase } from "@/cms/blocks/registry/types";
import type { ConfirmInvoker } from "../types";
import { getLocaleUI, getBlockLocaleStrings, getFileNameFromPath } from "../utils";
import { sanitizeTableDoc, TABLE_MAX_COLS, TABLE_MAX_ROWS, type TableBlockData, TABLE_SCHEMA_VERSION, createTableDoc } from "@/cms/blocks/table";
import { useAssetMeta, useCopyFeedback, useInert } from "./block-card/blockCardHooks";
import { dragTransition } from "./block-card/blockCard.constants";
import { BlockCardShell } from "./block-card/BlockCardShell";
import { BlockCardHeader } from "./block-card/BlockCardHeader";
import { BlockCardBody } from "./block-card/BlockCardBody";

type BlockCardProps = {
  block: BlockBase;
  locale: "en" | "fa";
  indexNumber?: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  selection?: {
    showCheckbox?: boolean;
    checked?: boolean;
    onCheckChange?: (checked: boolean) => void;
    disableCheckbox?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    highlightBorder?: boolean;
  };
  errorMessages?: string[];
  onChange: (patch: (data: any) => any) => void;
  onChangeMeta?: (patch: (meta: any) => any) => void;
  actions: {
    onRemove: () => void;
    onCopy: () => Promise<boolean>;
    onPickImage: () => void;
    hideCopyAction?: boolean;
    hideDeleteControl?: boolean;
  };
  drag?: {
    attributes: Record<string, any>;
    listeners: Record<string, any>;
    isDragging: boolean;
    handleOnly?: boolean;
  };
  metaFlags: {
    heroImageInUse: boolean;
    shortDescriptionInUse: boolean;
    editMode: boolean;
  };
  onRequestConfirm: ConfirmInvoker;
  countLabel?: string;
  showReorderIcon?: boolean;
};

export function BlockCard({
  block,
  locale,
  indexNumber = 0,
  collapsed = false,
  onToggleCollapse = () => {},
  selection,
  errorMessages,
  onChange,
  onChangeMeta,
  actions,
  drag,
  metaFlags,
  onRequestConfirm,
  countLabel,
  showReorderIcon,
}: BlockCardProps) {
  const {
    showCheckbox = false,
    checked = false,
    onCheckChange,
    disableCheckbox = false,
    isSelected = false,
    onSelect,
    highlightBorder = false,
  } = selection ?? {};
  const {
    onRemove,
    onCopy,
    onPickImage,
    hideCopyAction = false,
    hideDeleteControl = false,
  } = actions;
  const {
    attributes: dragAttributes = {},
    listeners: dragListeners = {},
    isDragging = false,
    handleOnly = false,
  } = drag ?? {};
  const dragHandleAttributes = handleOnly ? dragAttributes : {};
  const dragHandleListeners = handleOnly ? dragListeners : {};
  const shellDragAttributes = handleOnly ? {} : dragAttributes;
  const shellDragListeners = handleOnly ? {} : dragListeners;
  const {
    heroImageInUse,
    shortDescriptionInUse,
    editMode,
  } = metaFlags;
  const errors = errorMessages ?? [];
  const { dir: inputDir, alignClass: inputAlignment, inputClass: localizedInputClass } = getLocaleUI(locale);
  const localeStrings = React.useMemo(() => getBlockLocaleStrings(locale), [locale]);

  const isParagraph = block.type === "paragraph";
  const paragraphVariant = isParagraph ? ((block.data as any).variant ?? "default") : "default";
  const isShortDescription = paragraphVariant === "shortDescription";

  const inlineMeta = (block.meta as any)?.inline;
  const inlineEnabled = Boolean(inlineMeta?.enabled);
  const inlineAlign: "left" | "right" = inlineMeta?.align === "right" ? "right" : "left";
  const quoteVariant = block.type === "quote" ? ((block.data as any).variant ?? "default") : "default";
  const canInline =
    block.type === "image" || (block.type === "quote" && quoteVariant === "bracket");
  const showInlineControls = Boolean(onChangeMeta && canInline);

  const isImageBlock = block.type === "image";
  const imageVariant = isImageBlock ? ((block.data as any).variant ?? "default") : "default";
  const isHeroImage = imageVariant === "hero";
  const imageData = isImageBlock ? ((block.data as any) ?? {}) : undefined;
  const { assetMeta, clearImage } = useAssetMeta(isImageBlock, imageData, onChange);

  const isTableBlock = block.type === "table";
  const tableData = isTableBlock
    ? ((block.data as TableBlockData) ?? { schemaVersion: TABLE_SCHEMA_VERSION, doc: createTableDoc(), variant: "default" as const })
    : null;
  const [tableEditorOpen, setTableEditorOpen] = React.useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = React.useState(false);

  const previewSrc = imageData?.previewUrl ?? imageData?.url ?? assetMeta?.url ?? undefined;
  const displayName = imageData?.sourceName ?? assetMeta?.name ?? getFileNameFromPath(previewSrc);
  const displayWidth = imageData?.width ?? assetMeta?.width;
  const displayHeight = imageData?.height ?? assetMeta?.height;

  const contentRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  useInert(bodyRef, editMode);

  const { copied, handleCopy: triggerCopy } = useCopyFeedback(onCopy);

  const updateInline = React.useCallback(
    (enabled: boolean, align?: "left" | "right") => {
      if (!onChangeMeta) return;
      onChangeMeta((prev) => ({
        ...(prev ?? {}),
        inline: {
          enabled,
          align: align ?? inlineAlign,
        },
      }));
    },
    [inlineAlign, onChangeMeta],
  );

  const tableStats = React.useMemo(() => {
    if (!tableData) return null;
    try {
      const { stats } = sanitizeTableDoc(tableData.doc, { maxRows: TABLE_MAX_ROWS, maxCols: TABLE_MAX_COLS });
      return stats;
    } catch {
      return null;
    }
  }, [tableData]);

  const actionsMuted =
    ((!editMode && Boolean(onSelect) && !isSelected) ||
      (showCheckbox && !checked));

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerCopy();
  };

  const inlineGroup = {
    show: showInlineControls,
    enabled: inlineEnabled,
    align: inlineAlign,
    update: updateInline,
  } as const;

  const shortDescription = {
    value: isShortDescription,
    inUse: shortDescriptionInUse,
  } as const;

  const imageDisplay = {
    previewSrc: previewSrc,
    name: displayName,
    width: displayWidth,
    height: displayHeight,
  };

  const imageGroup = {
    isBlock: isImageBlock,
    isHero: isHeroImage,
    heroInUse: heroImageInUse,
    data: imageData,
    display: imageDisplay,
    onPick: onPickImage,
    clearImage,
  };

  const tableGroup = {
    data: tableData,
    stats: tableStats,
    editorOpen: tableEditorOpen,
    setEditorOpen: setTableEditorOpen,
  };

  const linkPicker = {
    open: linkPickerOpen,
    setOpen: setLinkPickerOpen,
  };

  return (
    <BlockCardShell
      inputDir={inputDir as "ltr" | "rtl"}
      highlightBorder={highlightBorder}
      isDragging={isDragging}
      editMode={editMode}
      hideCopyAction={hideCopyAction}
      actionsMuted={actionsMuted}
      copied={copied}
      handleCopy={handleCopy}
      hideDeleteControl={hideDeleteControl}
      onRemove={onRemove}
      isSelected={isSelected}
      onSelect={onSelect}
      dragAttributes={shellDragAttributes}
      dragListeners={shellDragListeners}
    >
      <motion.div
        layout="position"
        ref={contentRef}
        className={`relative z-0 space-y-3 transition ${editMode ? "opacity-85" : "opacity-100"}`}
        animate={{ scale: isDragging ? 1.01 : 1 }}
        transition={dragTransition}
      >
       <BlockCardHeader
         blockType={block.type}
         localeStrings={localeStrings}
         inputDir={inputDir as "ltr" | "rtl"}
         indexNumber={indexNumber}
         errors={errors}
         collapsed={collapsed}
         onToggleCollapse={onToggleCollapse}
         showCheckbox={showCheckbox}
         checked={checked}
         disableCheckbox={disableCheckbox}
         onCheckChange={onCheckChange}
         isDragging={isDragging}
         countLabel={countLabel}
         showReorderIcon={showReorderIcon}
         dragHandleAttributes={dragHandleAttributes}
         dragHandleListeners={dragHandleListeners}
       />

        <AnimatePresence initial={false}>
          {collapsed ? null : (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="pt-1" ref={bodyRef}>
                <BlockCardBody
                  block={block}
                  locale={locale}
                  localeStrings={localeStrings}
                  inputDir={inputDir as "ltr" | "rtl"}
                  inputAlignment={inputAlignment}
                  localizedInputClass={localizedInputClass}
                  onChange={onChange}
                  onChangeMeta={onChangeMeta}
                  onRequestConfirm={onRequestConfirm}
                  inline={inlineGroup}
                  shortDescription={shortDescription}
                  image={imageGroup}
                  table={tableGroup}
                  linkPicker={linkPicker}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </BlockCardShell>
  );
}
